import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { collection, query, where, getDocs, getFirestore } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import '../styles/SectionContent.css';
import '../styles/Reports.css';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const Reports = () => {
  const { currentUser } = useAuth();
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState({
    labels: [],
    clinicianAdded: [],
    selfRegistered: []
  });

  useEffect(() => {
    const fetchRegistrationData = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        const db = getFirestore();
        const patientsRef = collection(db, 'patients');
        const q = query(patientsRef, where('clinicianId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        // Process data for the selected time range
        const now = new Date();
        const cutoffDate = new Date(now);
        
        if (timeRange === '7d') cutoffDate.setDate(now.getDate() - 7);
        else if (timeRange === '30d') cutoffDate.setDate(now.getDate() - 30);
        else if (timeRange === '90d') cutoffDate.setDate(now.getDate() - 90);
        
        // Initialize data structure
        const days = [];
        const clinicianAdded = [];
        const selfRegistered = [];
        
        // Group registrations by day
        const registrationsByDay = {};
        const daysInRange = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
        
        // Initialize with zeros
        for (let i = daysInRange - 1; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(now.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          registrationsByDay[dateStr] = { clinician: 0, self: 0 };
          days.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
        }
        
        // Process each patient
        console.log('Processing patients...');
        querySnapshot.forEach((doc) => {
          const patient = doc.data();
          console.log('Patient data:', patient);
          
          // Handle different possible date formats
          let createdAt = new Date(0);
          if (patient.createdAt) {
            createdAt = patient.createdAt.toDate ? patient.createdAt.toDate() : new Date(patient.createdAt);
          }
          
          console.log('Patient createdAt:', createdAt, 'Cutoff date:', cutoffDate);
          
          if (createdAt >= cutoffDate) {
            const dateStr = createdAt.toISOString().split('T')[0];
            console.log('Processing patient for date:', dateStr, 'Self-registered:', patient.selfRegistered);
            
            // Initialize day if it doesn't exist
            registrationsByDay[dateStr] = registrationsByDay[dateStr] || { clinician: 0, self: 0 };
            
            // Check for self-registered patients by looking at createdBy field
            const isSelfRegistered = patient.createdBy === 'self_registered';
            
            if (isSelfRegistered) {
              console.log('Incrementing self-registered count for', dateStr);
              registrationsByDay[dateStr].self++;
            } else {
              console.log('Incrementing clinician-added count for', dateStr);
              registrationsByDay[dateStr].clinician++;
            }
          }
        });
        
        // Convert to arrays for the chart
        Object.values(registrationsByDay).forEach(dayData => {
          clinicianAdded.push(dayData.clinician);
          selfRegistered.push(dayData.self);
        });
        
        setRegistrationData({
          labels: days,
          clinicianAdded,
          selfRegistered
        });
        
      } catch (error) {
        console.error('Error fetching registration data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRegistrationData();
  }, [currentUser, timeRange]);
  
  const chartData = {
    labels: registrationData.labels,
    datasets: [
      {
        label: 'Clinician Added',
        data: registrationData.clinicianAdded,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.3,
        fill: true
      },
      {
        label: 'Self Registered',
        data: registrationData.selfRegistered,
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        tension: 0.3,
        fill: true
      }
    ]
  };
  
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Patient Registrations Over Time',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };
  
  const totalRegistrations = registrationData.clinicianAdded.reduce((a, b) => a + b, 0) + 
                           registrationData.selfRegistered.reduce((a, b) => a + b, 0);
  const clinicianAddedTotal = registrationData.clinicianAdded.reduce((a, b) => a + b, 0);
  const selfRegisteredTotal = registrationData.selfRegistered.reduce((a, b) => a + b, 0);

  return (
    <div className="section-content">
      <div className="section-header">
        <h2>Analytics & Reports</h2>
        <div className="time-range-selector">
          <button 
            className={`time-range-btn ${timeRange === '7d' ? 'active' : ''}`}
            onClick={() => setTimeRange('7d')}
          >
            7D
          </button>
          <button 
            className={`time-range-btn ${timeRange === '30d' ? 'active' : ''}`}
            onClick={() => setTimeRange('30d')}
          >
            30D
          </button>
          <button 
            className={`time-range-btn ${timeRange === '90d' ? 'active' : ''}`}
            onClick={() => setTimeRange('90d')}
          >
            90D
          </button>
        </div>
      </div>
      
      <div className="report-card">
        <h3>Patient Registrations</h3>
        
        {loading ? (
          <div className="loading-spinner">Loading registration data...</div>
        ) : (
          <>
            <div className="registration-stats">
              <div className="stat-card">
                <div className="stat-value">{totalRegistrations}</div>
                <div className="stat-label">Total Registrations</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{clinicianAddedTotal}</div>
                <div className="stat-label">Clinician Added</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{selfRegisteredTotal}</div>
                <div className="stat-label">Self Registered</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {totalRegistrations > 0 
                    ? `${Math.round((selfRegisteredTotal / totalRegistrations) * 100)}%` 
                    : '0%'}
                </div>
                <div className="stat-label">Self-Registration Rate</div>
              </div>
            </div>
            
            <div className="chart-container">
              <Line data={chartData} options={chartOptions} />
            </div>
            
            <div className="report-actions">
              <button className="export-btn">
                <i className="fas fa-download"></i> Export as CSV
              </button>
              <button className="export-btn">
                <i className="fas fa-file-pdf"></i> Export as PDF
              </button>
            </div>
          </>
        )}
      </div>
      
      <div className="content-grid">
        <div className="content-card">
          <div className="card-icon">📈</div>
          <h3>Patient Outcomes</h3>
          <p>Track treatment effectiveness and patient recovery rates</p>
          <div className="card-stats">
            <span>Last 30 days</span>
            <span>Updated</span>
          </div>
        </div>
        
        <div className="content-card">
          <div className="card-icon">⏰</div>
          <h3>Appointment Analytics</h3>
          <p>Monitor appointment scheduling and attendance patterns</p>
          <div className="card-stats">
            <span>This month</span>
            <span>Active</span>
          </div>
        </div>
        
        <div className="content-card">
          <div className="card-icon">💰</div>
          <h3>Financial Summary</h3>
          <p>Revenue tracking and billing analytics</p>
          <div className="card-stats">
            <span>Q4 2024</span>
            <span>Pending</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
